<template>
  <div :class="$style.conditionsSection">
    <div :class="$style.inclusionOptions">
      <DropDownSelect
        :class="$style.inclusionDropDown"
        data-test-id="inclusionSelector"
        name="Inclusion"
        :options="inclusionOptions"
        :value="inclusion"
        @input="(value) => setCondition('inclusion', value)"
        @open="handleInclusionOpen"
      />
      <ParagraphBody>
        of the following conditions must match for this policy to apply to included users
      </ParagraphBody>
    </div>

    <div
      v-for="row in selectedOptions"
      :key="row.id"
      :class="$style.rowContainer"
      data-test-id="row"
    >
      <div>
        {{ row.label }}
      </div>

      <div :class="$style.rowSelections">
        <div
          :class="$style.rowSelectionGroup"
        >
          <component
            :is="row.component"
            :class="$style.rowSelection"
            :inclusion="inclusion"
            :name="row.key"
            :value="getCondition(row.value)"
            @input="row.inputHandler"
          />
        </div>

        <transition :name="$style.fade">
          <div
            v-if="row.errorMessage"
            :class="$style.error"
          >
            {{ row.errorMessage }}
          </div>
        </transition>
      </div>

      <div
        :class="$style.actions"
      >
        <IconButton
          data-test-id="deleteButton"
          title="Delete"
          @click="unselectOption(row.key)"
        >
          <TrashIcon />
        </IconButton>
      </div>
      <GuideMessage
        v-if="shouldShowGuide(row)"
        caretPosition="left"
        :class="$style.conditionInfoGuide"
        name="conditionInfo"
      />
    </div> <!-- /rowContainer -->

    <DropDownMenu
      data-test-id="addCondition"
      :disabled="hasMenuOptions"
      itemSection="conditions"
      itemType="authNPoliciesAside"
      :menuSections="menuOptions"
      menuTitle="add condition"
      :onItemClick="selectAMenuOption"
      useButtonToActivate
      @open="handleConditionsDropDownOpen"
    />

    <Warning
      v-if="certDistributionIsDisabledWarning"
      :class="$style.warningEnableCertDistributionPanel"
      data-test-id="warningCertDistributionIsDisabled"
    >
      Device certificate distribution has been disabled.
      <span
        :class="$style.warningEnableCertDistributionLinkText"
        data-test-id="warningEnableCertDistributionLink"
        @click="handleEnableCertDistributionClick"
      >
        Turn on device certificate distribution
      </span>
      or remove device conditions to ensure uninterrupted access for your users.
    </Warning>
  </div>
</template>

<script>
import { ItemTypeConsts, StoreModelMap, StoreModuleMap } from 'client/util/Constants';
import { ParagraphBody } from '@jumpcloud/ui-components';
import {
  eventLocations,
  eventProperties,
} from 'client/GrowthEngineering/utilities/GrowthConstants';
import {
  filter, get, map, reduce, set, toArray,
} from 'lodash';
import { getMessageForError } from 'client/components/FormValidation/helpers/validationHelpers';
import { mapActions, mapState } from 'vuex';
import CertificateSettingsModal from 'client/Security/CertificateSettingsModal.vue';
import DropDownMenu from 'client/components/DropDownMenu/DropDownMenu.vue';
import DropDownSelect from 'client/components/DropDownMenu/DropDownSelect.vue';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import GuideMessage from 'client/components/GuideMessage/GuideMessage.vue';
import IconButton from 'client/components/Buttons/IconButton.vue';
import ModalEventBus from 'components/Modal/ModalEventBus';
import TrashIcon from 'public/img/icons/trash.svg?inline';
import Warning from 'components/Warning/Warning.vue';

// Conditions
import { ConditionInclusion } from 'client/Security/data/Condition';
import ConditionItems from 'client/Security/data/ConditionItems';

// Condition Row Components
import ConditionRowDevice from 'client/Security/AuthNPoliciesAsidePanel/ConditionsSectionLegacy/ConditionRowDevice.vue';
import ConditionRowDeviceEncrypted from 'client/Security/AuthNPoliciesAsidePanel/ConditionsSectionLegacy/ConditionRowDeviceEncrypted.vue';
import ConditionRowIpList from 'client/Security/AuthNPoliciesAsidePanel/ConditionsSectionLegacy/ConditionRowIpList.vue';
import ConditionRowLocations from 'client/Security/AuthNPoliciesAsidePanel/ConditionsSectionLegacy/ConditionRowLocations.vue';

const orgModel = StoreModelMap[ItemTypeConsts.organizations];
const settingsModule = StoreModuleMap[ItemTypeConsts.settings];
const { conditionalPoliciesAside } = eventLocations;
const {
  description,
  location,
  section,
  text,
} = eventProperties;

export default {
  name: 'ConditionsSectionLegacy',

  inject: ['$validator'],

  components: {
    ConditionRowDevice,
    ConditionRowDeviceEncrypted,
    ConditionRowIpList,
    ConditionRowLocations,
    DropDownMenu,
    IconButton,
    DropDownSelect,
    GuideMessage,
    ParagraphBody,
    TrashIcon,
    Warning,
  },

  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      inclusion: 'all',
      expressions: {},

      selections: [],

      // tracking the initial state of cert distribution to work around the org
      // model being updated before commit (see client/Security/CertificateSettings.vue)
      certDistributionIsEnabledState: false,
    };
  },

  computed: {
    ...mapState(orgModel, { organization: 'item' }),
    ...mapState(settingsModule, ['orgId']),
    ...mapState('GuidedSetupModule', ['guideName']),

    certDistributionIsDisabledWarning() {
      return !this.certDistributionIsEnabledState
        && filter(this.selectedOptions, option => option.key === 'device').length > 0;
    },

    certDistributionIsEnabled() {
      return this.organization.settings
        ? this.organization.settings.deviceIdentificationEnabled
        : false;
    },

    hasMenuOptions() {
      return !this.menuOptions[0].menuItems.length;
    },

    inclusionOptions() {
      return [
        { name: 'all', value: ConditionInclusion.all },
        { name: 'any', value: ConditionInclusion.any },
      ];
    },

    menuOptions() {
      return [{
        menuItems: map(
          filter(
            this.unselectedOptions, unselectOption => unselectOption.canBeShown,
          ),
          option => ({
            key: option.key,
            label: option.label,
          }),
        ),
      }];
    },

    // Right now we're still defining the final interface for these components.
    // TODO: Once the interface is established, we can make this
    //        a component, type or other defined structure. - Chad
    options() {
      return {
        device: {
          key: 'device',
          label: 'Device',

          component: ConditionRowDevice,
          value: 'expressions.device',
          inputHandler: (condition) => { this.setCondition('expressions.device', condition); },

          errorMessage: getMessageForError(this.$validator.errors, 'device'),

          beforeSelect: () => {
            // We now do a selection on load, which sometimes is before we can update the org
            // (going direct to the url). If so, don't show the modal.
            if (!this.certDistributionIsEnabled) {
              this.openCertificateSettingsModal({
                // Here we trigger a 'refresh' of the device list since
                // the modal will update this.certDistributionIsEnabled.
                handleContinue: () => {
                  this.selectAMenuOption({ key: 'device' });
                  // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                  this.certDistributionIsEnabledState = this.certDistributionIsEnabled;
                },
              });
            }

            return this.certDistributionIsEnabled;
          },
          canBeShown: true,
        },
        // Full Disc Encryption
        deviceEncrypted: {
          key: 'deviceEncrypted',
          label: 'Disk Encryption',

          component: ConditionRowDeviceEncrypted,
          value: 'expressions.deviceEncrypted',
          inputHandler: (condition) => { this.setCondition('expressions.deviceEncrypted', condition); },

          errorMessage: getMessageForError(this.$validator.errors, 'deviceEncrypted'),

          beforeSelect: () => {
            // We now do a selection on load, which sometimes is before we can update the org
            // (going direct to the url). If so, don't show the modal.
            if (!this.certDistributionIsEnabled) {
              this.openCertificateSettingsModal({
                // Here we trigger a 'refresh' of the device list since
                // the modal will update this.certDistributionIsEnabled.
                handleContinue: () => {
                  this.selectAMenuOption({ key: 'deviceEncrypted' });
                  // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                  this.certDistributionIsEnabledState = this.certDistributionIsEnabled;
                },
              });
            }

            return this.certDistributionIsEnabled;
          },
          canBeShown: true,

        },
        ipAddresses: {
          key: 'ipAddresses',
          label: 'IP Address',

          component: ConditionRowIpList,
          value: 'expressions.ipAddresses',
          inputHandler: (condition) => { this.setCondition('expressions.ipAddresses', condition); },

          errorMessage: getMessageForError(this.$validator.errors, 'ipAddresses'),

          beforeSelect: () => true,
          canBeShown: true,
        },
        locations: {
          key: 'locations',
          label: 'Location',

          component: ConditionRowLocations,
          value: 'expressions.locations',
          inputHandler: (condition) => { this.setCondition('expressions.locations', condition); },

          errorMessage: getMessageForError(this.$validator.errors, 'locations'),

          beforeSelect: () => true,
          canBeShown: true,
        },
      };
    },

    selectedOptions() {
      return filter(this.options, (option, key) => this.selections.includes(key));
    },

    unselectedOptions() {
      return filter(this.options, (option, key) => !this.selections.includes(key));
    },
  },

  watch: {
    organization: {
      handler(newVal) {
        // Settings is present after actually fetching the org, We need to know the org's settings
        // in order to correctly display or not display the warning to turn on device idenficiation.
        if ('settings' in this.organization) {
          this.certDistributionIsEnabledState = newVal.settings.deviceIdentificationEnabled;
          this.preselectCondition();
        }
      },
    },
    // Need to do this when the org loads in finally...
    selections: {
      handler(newValue) {
        if (!newValue.includes(this.guideName)) {
          this.dismissGuide();
        }
      },
    },
    value: {
      handler(newValue) {
        // Set the condition inclusion clause.
        this.inclusion = get(newValue, 'conditions.inclusion', 'all');
        // Set the values for any existing
        const externalExpressions = get(newValue, 'conditions.expressions', {});
        this.expressions = reduce(externalExpressions, (expressions, expression, key) => (
          { ...expressions, [key]: ConditionItems[key](expression) }
        ), {});

        // Set the selected rows for any existing expressions.
        this.selections = map(this.expressions, (expression, key) => key);
      },
      immediate: true,
    },
  },

  created() {
    this.fetchOrganization(this.orgId);
  },

  beforeMount() {
    this.certDistributionIsEnabledState = this.certDistributionIsEnabled;
  },

  mounted() {
    if ('settings' in this.organization) {
      this.preselectCondition();
    }
  },

  methods: {
    ...mapActions(orgModel, { fetchOrganization: 'fetchItem' }),
    ...mapActions('GuidedSetupModule', { dismissGuide: 'dismissGuide' }),

    getCondition(key) {
      return get(this, key);
    },

    handleConditionsDropDownOpen() {
      EventHelper.triggerButtonClicked({
        [description]: 'click the Condition Dropdown menu to see the options',
        [location]: conditionalPoliciesAside,
        [section]: 'conditions',
        [text]: 'add condition',
      });
    },

    handleEnableCertDistributionClick() {
      this.openCertificateSettingsModal({
        handleContinue: () => {
          this.certDistributionIsEnabledState = this.certDistributionIsEnabled;
        },
      });
    },

    handleInclusionOpen(selectValue) {
      EventHelper.triggerButtonClicked({
        [description]: 'Clicked on condition operator dropdown list',
        [location]: conditionalPoliciesAside,
        [section]: 'conditions',
        [text]: `condition operator dropdown ${selectValue}`,
      });
    },

    handleValueWasUpdated() {
      this.$emit('input', {
        ...this.value,
        conditions: {
          inclusion: this.inclusion,
          expressions: toArray(
            filter(this.expressions, (expression, key) => this.selections.includes(key)),
          ),
        },
      });
    },

    openCertificateSettingsModal({ handleContinue }) {
      return ModalEventBus.open({
        isLargeModal: true,
        component: CertificateSettingsModal,
        preventCloseOnOverlayClick: true,
        componentProps: {
          handleContinue,
        },
      });
    },

    preselectCondition() {
      if (this.guideName) {
        const { key, label } = this.options[this.guideName];
        this.selectAMenuOption({ key, label });
      }
    },

    selectAMenuOption(menuOption) {
      if (this.selections.indexOf(menuOption.key)) {
        if (this.options[menuOption.key].beforeSelect()) {
          this.selections.push(menuOption.key);
        }
      }
      EventHelper.triggerButtonClicked({
        [description]: 'click the Condition Dropdown menu to see the options',
        [location]: conditionalPoliciesAside,
        [text]: 'select / unselect',
        [section]: 'conditions',
      });
    },

    setCondition(key, value) {
      set(this, key, value);
      this.handleValueWasUpdated();

      // isolating this event to just the inclusion value (any/all)
      if (typeof value === 'string') {
        EventHelper.triggerDropDownItemSelected({
          [description]: 'Selects an item from dropdown',
          [location]: conditionalPoliciesAside,
          [section]: 'conditions',
          [text]: value,
        });
      }
    },

    shouldShowGuide(row) {
      const { key } = row;
      return key && this.guideName && key.toLowerCase() === this.guideName.toLowerCase();
    },

    unselectOption(option) {
      this.selections = filter(this.selections, selectedOption => selectedOption !== option);

      this.handleValueWasUpdated();
    },
  },
};
</script>

<style module>
  @import "../../../components/FormValidation/css/formFields.css";
  @import "../../../components/FormValidation/css/FormValidationFields.css";
  @import '../../../css/sections.css';

  @value (jcInformationalBase, jcGrey800) from '~css/brand-colors.css';

  .conditionsSection {
    --vertical-margin: 1.5em;
  }

  .conditionInfoGuide {
    left: 405px;
    top: -116px;
  }

  .inclusionOptions {
    align-items: center;
    display: flex;
    margin-bottom: var(--vertical-margin);
  }

  .rowContainer {
    align-items: center;
    display: grid;
    grid-template-columns: 9em auto 50px;
    margin-bottom: var(--vertical-margin);
  }

  .rowSelections {
    display: flex;
    flex-direction: column;
  }

  .rowSelectionGroup {
    display: flex;
    margin-bottom: 0.2em;
  }

  .rowSelection {
    align-items: center;
    display: flex;
    flex-direction: row;
  }

  .rowSelection:not(:last-child) {
    margin-right: 0.2em;
  }

  .actions {
    text-align: center;
  }

  .statusLabel {
    color: jcGrey800;
    font-weight: 700;
    text-transform: capitalize;
  }

  .inclusionDropDown {
    margin-right: 1em;
  }

  .warningEnableCertDistributionPanel {
    margin: 2em 0 1em;
  }

  .warningEnableCertDistributionLinkText {
    color: jcInformationalBase;
    cursor: pointer;
    text-decoration: underline;
  }

  .warningEnableCertDistributionLinkText:hover {
    color: jcInformationalBase;
  }
</style>
